import { AfterViewInit, Component, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription, of, throwError } from 'rxjs';
import { mergeMap, retry } from 'rxjs/operators';
import { Constant, DefaultGUID, RoleName, attachmentOf, entityTableName, fileType } from 'src/app/@shared/constants/Constant';
import { ConfirmationMsg } from 'src/app/@shared/constants/confirmation-msg.constant';
import { ILoggedInUserViewModel } from 'src/app/model/common.model';
import {
  IChatGroupMembers,
  IFileInfo,
  IGroupList,
  IInternalMessageModel,
  ILoadGroupChatPagePayload,
  IMessageStatusModel,
  IOpenGroupInfoPayload,
  IRemoveChatGroupMember,
  ISendMessageModel,
} from 'src/app/model/internalGroupChat.model';
import { PreviewFileConfig } from 'src/app/model/preview-file.model';
import { FilesService } from 'src/app/service/files.service';
import { MapMarkerService } from 'src/app/service/map-marker.service';
import { ViewMemberModel } from 'src/app/service/memeber.model';
import { ServiceRequestService } from 'src/app/service/service-request.service';
import { SignalrService } from 'src/app/sse/signalr.service';
import { GroupInfoDialogComponent } from '../Dialogs/group-info-dialog/group-info-dialog.component';
import { InternalGroupChatDialogComponent } from '../Dialogs/internal-group-chat-dialog/internal-group-chat-dialog.component';
import { MemberInfoDialogComponent } from '../Dialogs/member-info-dialog/member-info-dialog.component';
import { OpenImageDialogComponent } from '../Dialogs/open-image-dialog/open-image-dialog.component';
import { InternalGroupChatService } from '../internal-group-chat.service';

declare var $: any;
@Component({
  selector: 'app-message-panel',
  templateUrl: './message-panel.component.html',
  styleUrls: ['./message-panel.component.css'],
})
export class MessagePanelComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input('internalMemberList') internalMemberList: ViewMemberModel[];
  private _subscriptions: Subscription = new Subscription();
  _fileUploadSubscription: Subscription = new Subscription();
  loader: boolean;
  timeZone: any;
  user: ILoggedInUserViewModel;
  isCustomer: boolean;
  isVendor: boolean;
  isInternal: boolean;
  isMoreOptionVisible: boolean = false;
  toggleEmojiPicker: boolean = false;
  textMessageBody: string = '';
  messageList: IInternalMessageModel[] = [];
  groupMemberList: IChatGroupMembers[] = [];
  groupMemberListOnHeaderLoader: boolean;
  memberListToShowOnHeader: string[] = [];
  loadGroupChatPayload: ILoadGroupChatPagePayload;
  showMessagePanelForSmallScreen: boolean;
  exitGroupPayload: IRemoveChatGroupMember;
  isDeleteGroupOptionVisible: boolean;
  isLoggedInMemberAGroupAdmin: boolean;
  attachment: File = null;
  attachmentFileName: string = null;
  attachmentType: string[] = [];
  imageURL: string;
  imagePreview: boolean;
  sendMessageBtnDisabled: boolean = true;
  isUploadFileLoading: boolean;
  fileAttachment: string;
  profilePicLoader: boolean;
  messageAttachmentLoader: boolean;
  isRefreshing: boolean = false;
  constructor(
    private _dialog: MatDialog,
    private _router: Router,
    private _toast: ToastrService,
    private _serviceRequestService: ServiceRequestService,
    private _internalGroupChatService: InternalGroupChatService,
    private _mapMarkerService: MapMarkerService,
    private _fileService: FilesService,
    private _signalRService: SignalrService,
    @Optional() private _dialogRef: MatDialogRef<InternalGroupChatDialogComponent>,
  ) {
    this._subscriptions.add(
      this._serviceRequestService.timZoneChange.subscribe((value) => {
        this.timeZone = Constant.selectedTimeZoneShortValue;
      }),
    );
  }

  ngOnInit() {
    this.user = JSON.parse(localStorage.getItem('user'));
    this.isInternal =
      this.user.role === RoleName.ExecutiveAdmin || this.user.role === RoleName.Coordinator || this.user.role === RoleName.Management;
    this.isCustomer = this.user.role === RoleName.CustomerAdmin || this.user.role === RoleName.CustomerCoordinator;
    this.isVendor = this.user.role === RoleName.VendorAdmin || this.user.role === RoleName.VendorCoordinator;
    this.refreshMessagePanel();
    this.pushNewMessagesOnUI();
    this._subscriptions.add(
      this._internalGroupChatService.updateGroupInfoAction.subscribe((response) => {
        if (response !== null && response.groupDetail.groupName !== null && response.groupDetail.groupName.trim().length > 0) {
          this.loadGroupChatPayload.groupDetail.groupName = response.groupDetail.groupName;
          this.groupMemberList = response.memberList;
          this.memberListToShowOnHeader = [];
          this.groupMemberList.map((member) => {
            this.memberListToShowOnHeader.push(!member.isGroupAdmin ? member.memberFirstName : `${member.memberFirstName} (Admin)`);
          });
        }
      }),
    );
  }

  ngAfterViewInit(): void {
    this._subscriptions.add(
      this._serviceRequestService.timZoneChange.subscribe((value) => {
        this.timeZone = Constant.selectedTimeZoneShortValue;
      }),
    );
    if (window.innerWidth <= 991) {
      this._subscriptions.add(
        this._internalGroupChatService.toggleBetweenChatListAndMessagePanelAction.subscribe((response) => {
          this.showMessagePanelForSmallScreen = response !== null && response !== false;
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._fileUploadSubscription.unsubscribe();
    this._internalGroupChatService.loadGroupChatSubject$.next({
      groupDetail: null,
      serviceRequestDetail: null,
    });
    if (window.innerWidth <= 991) {
      this._internalGroupChatService.toggleBetweenChatListAndMessagePanelSubject$.next(false);
    }
  }

  reinitializeMessagePage() {
    this.textMessageBody = null;
    this.groupMemberList = [];
    this.memberListToShowOnHeader = [];
    this.groupMemberListOnHeaderLoader = true;
  }

  private reinitializeSubscription() {
    this._subscriptions.unsubscribe();
    this._subscriptions = new Subscription();
    this._fileUploadSubscription.unsubscribe();
    this._fileUploadSubscription = new Subscription();
  }

  refreshMessagePanel() {
    this.reinitializeSubscription();
    this._subscriptions.add(
      this._internalGroupChatService.loadGroupChatAction.subscribe((response) => {
        this.loadGroupChatPayload = null;
        this.loadGroupChatPayload = JSON.parse(JSON.stringify(response));
        this.textMessageBody = null;
        this.closeImagePreview();
        if (this.loadGroupChatPayload !== null) {
          if (this.loadGroupChatPayload.groupDetail === null && this.loadGroupChatPayload.serviceRequestDetail === null) {
            this.messageList = [];
            this.loadGroupChatPayload = null;
          } else if (this.loadGroupChatPayload.groupDetail !== null && this.loadGroupChatPayload.serviceRequestDetail !== null) {
            this.reinitializeMessagePage();
            this.getGroupMemberList(this.loadGroupChatPayload.groupDetail.chatGroupId);
            this.getAllMessages(this.loadGroupChatPayload.groupDetail.chatGroupId, this.user.memberId);
          }
        }
      }),
    );
  }

  pushNewMessagesOnUI() {
    this._subscriptions.add(
      this._internalGroupChatService.newMessageAction.subscribe((response) => {
        if (
          this.loadGroupChatPayload &&
          this.loadGroupChatPayload.serviceRequestDetail &&
          this.loadGroupChatPayload.groupDetail &&
          response
        ) {
          if (
            this.loadGroupChatPayload.serviceRequestDetail.serviceRequestId === response.serRequestId &&
            this.loadGroupChatPayload.groupDetail.chatGroupId === response.chatGroupId
          ) {
            this.messageList.push(response.messageData);
            this.scrollToElementById(null);
          }
        }
      }),
    );
  }

  getAllMessages(chatGroupID: string, loggedInMemberID: string) {
    this._subscriptions.add(
      this._internalGroupChatService.getAllMessages(chatGroupID, loggedInMemberID).subscribe(
        (response) => {
          if (this.groupMemberList) {
            this.isRefreshing = false;
          }
          if (!this.areArrayIdentical(this.messageList, response.data)) {
            this.messageList = response.data;
            this.getMessageAttachments();
            this.markAllMessageAsRead({ chatGroupId: chatGroupID, memberId: loggedInMemberID }, 5);
            this.messageList.map((message) => {
              message.isSending = false;
              message.isSentSuccessful = true;
            });
          }
        },
        () => {
          this.loader = false;
          this._toast.warning('Unable to get the messages!', 'Warning', {
            timeOut: 6000,
            positionClass: 'toast-top-center',
          });
          this.isRefreshing = false;
        },
      ),
    );
  }

  getMessageAttachments() {
    const fileNameList: string[] = [];
    this.messageList.map((message) => {
      if (message.fileName) {
        fileNameList.push(message.fileName);
      }
    });
    if (fileNameList.length > 0) {
      this.getAllFilesPathToShowOnUI(fileNameList, attachmentOf.messageAttachment);
    } else if (this.loadGroupChatPayload?.groupDetail.lastMessageId) {
      this.scrollToElementById(this.loadGroupChatPayload.groupDetail.lastMessageId);
    }
  }

  scrollToElementById(elementId: string) {
    const element = document.getElementById(elementId);
    if (elementId && element) {
      element.scrollIntoView({ behavior: 'smooth' });
      this.loadGroupChatPayload.groupDetail.lastMessageId = null;
    } else if (!elementId) {
      const messagePanel = document.getElementById('message-panel');
      const timer = setTimeout(() => {
        if (messagePanel) {
          messagePanel.scrollTo({
            top: messagePanel.scrollHeight,
            behavior: 'smooth',
          });
        }
        clearTimeout(timer);
      }, 50);
    }
  }

  messageTrackBy(index: number, message: IInternalMessageModel) {
    return message.messageId;
  }

  getGroupMemberList(chatGroupID) {
    this.loader = true;
    this._subscriptions.add(
      this._internalGroupChatService.getGroupMemberDetails(chatGroupID).subscribe(
        (response) => {
          this.loader = false;
          if (this.messageList) {
            this.isRefreshing = false;
          }
          this.groupMemberList = response.data;
          const colorList = this._mapMarkerService.generateUniqueRouteColors(this.groupMemberList.length);
          this.groupMemberList.map((member, i) => {
            member.memberProfileColor = colorList[i];
          });
          const fileNameList: string[] = [];
          this.groupMemberList.map((member) => {
            if (member.profilePicFileName) {
              fileNameList.push(member.profilePicFileName);
            }
          });
          this.getAllFilesPathToShowOnUI(fileNameList, attachmentOf.profilePic);
          this.isLoggedInMemberAGroupAdmin = this.groupMemberList.some(
            (member) => member.memberId === this.user.memberId && member.isGroupAdmin === true,
          );
          this.isDeleteGroupOptionVisible = this.isInternal && this.isLoggedInMemberAGroupAdmin;
          this.memberListToShowOnHeader = [];
          this.groupMemberList.map((member) => {
            this.memberListToShowOnHeader.push(!member.isGroupAdmin ? member.memberFirstName : `${member.memberFirstName} (Admin)`);
          });
          this.groupMemberListOnHeaderLoader = false;
        },
        () => {
          this.loader = false;
          this._toast.warning('Unable to get the member details!', 'Warning', {
            timeOut: 6000,
            positionClass: 'toast-top-center',
          });
          this.isRefreshing = false;
        },
      ),
    );
  }

  getAllFilesPathToShowOnUI(fileNameList: string[], forWhichTypeOfFile?: string) {
    if (forWhichTypeOfFile === attachmentOf.profilePic) {
      this.profilePicLoader = true;
    }
    if (forWhichTypeOfFile === attachmentOf.messageAttachment) {
      this.messageAttachmentLoader = true;
    }
    this._subscriptions.add(
      this._internalGroupChatService.getAllUploadedFiles(fileNameList).subscribe(
        (response) => {
          if (forWhichTypeOfFile === attachmentOf.profilePic) {
            this.assignMemberProfilePic(response.data);
            this.profilePicLoader = false;
          }
          if (forWhichTypeOfFile === attachmentOf.messageAttachment) {
            this.assignMessageAttachment(response.data);
            this.messageAttachmentLoader = false;
          }
        },
        () => {
          if (forWhichTypeOfFile === attachmentOf.messageAttachment) {
            this.scrollToElementById(this.loadGroupChatPayload?.groupDetail.lastMessageId);
            this.messageAttachmentLoader = false;
            this._toast.warning('Unable to get the images!', 'Warning', {
              timeOut: 6000,
              positionClass: 'toast-top-center',
            });
          }
          if (forWhichTypeOfFile === attachmentOf.profilePic) {
            this.profilePicLoader = false;
          }
        },
      ),
    );
  }

  assignMemberProfilePic(fileList: IFileInfo[]) {
    this.groupMemberList.forEach((member) => {
      const file: IFileInfo = fileList.find((file) => file.fileName === member.profilePicFileName);
      if (file) {
        member.profilePicFilePath = file.filePath;
      } else {
        member.profilePicFilePath = null;
      }
    });
  }

  assignMessageAttachment(fileList: IFileInfo[]) {
    this.messageList.forEach((message, index) => {
      const file: IFileInfo = fileList.find((file) => file.fileName === message.fileName);
      if (file) {
        message.filePath = file.filePath;
      } else {
        message.filePath = null;
      }

      if (this.messageList.length - 1 === index && this.loadGroupChatPayload?.groupDetail.lastMessageId) {
        this.scrollToElementById(this.loadGroupChatPayload?.groupDetail.lastMessageId);
      }
    });
  }

  areArrayIdentical(previousMessageList: IInternalMessageModel[], currentMessageList: IInternalMessageModel[]): boolean {
    if (previousMessageList.length > 0 && currentMessageList.length > 0) {
      if (previousMessageList.length !== currentMessageList.length) {
        return false;
      } else {
        for (let i = 0; i < currentMessageList.length; i++) {
          const obj1: IInternalMessageModel = previousMessageList[i];
          const obj2: IInternalMessageModel = currentMessageList[i];
          if (obj1.messageId !== obj2.messageId) {
            return false;
          }
        }
        return true;
      }
    }
    return false;
  }

  memberProfileColor(senderMemberID: string): string {
    let hexColorCode: string;
    const memberDetail: IChatGroupMembers = this.groupMemberList.find((member) => member.memberId === senderMemberID);
    if (memberDetail) {
      hexColorCode = memberDetail.memberProfileColor;
    }
    return hexColorCode;
  }

  markAllMessageAsRead(readAllMessagePayload: IMessageStatusModel, retryCount) {
    this._internalGroupChatService
      .markAllMessagesRead(readAllMessagePayload)
      .pipe(
        mergeMap(
          (readResponse) => {
            return of(readResponse);
          },
          (e) => {
            throwError((err) => err);
          },
        ),
        retry(retryCount),
      )
      .subscribe();
  }

  gotoItineraryPage(serviceRequestId: string) {
    this._dialogRef?.close();
    if (this.isCustomer || this.isVendor) {
      this._router.navigate([`/common-itinerary/${serviceRequestId}/group-chat`]);
    } else {
      this._router.navigate([`/track-itinerary/${serviceRequestId}/ServiceRequest`]);
    }
  }

  openGroupInfoDialog(groupMemberList: IChatGroupMembers[], groupDetail: IGroupList): void {
    const dialogRef = this._dialog.open(GroupInfoDialogComponent, {
      maxWidth: '80rem',
      width: '100%',
      minWidth: '21rem',
      data: {
        memberList: groupMemberList,
        groupDetail: groupDetail,
        serviceRequestDetail: this.loadGroupChatPayload.serviceRequestDetail,
        internalMemberList: this.internalMemberList,
      } as IOpenGroupInfoPayload,
      height: 'fit-content',
    });
  }

  openMemberInfoDialog(message: IInternalMessageModel) {
    if (this.isInternal) {
      const memberDetail: IChatGroupMembers = this.groupMemberList.find((member) => member.memberId === message.memberId);
      if (memberDetail) {
        const dialogRef = this._dialog.open(MemberInfoDialogComponent, {
          maxWidth: '65rem',
          width: '100%',
          minWidth: '21rem',
          data: memberDetail,
          height: 'fit-content',
        });
      }
    }
  }

  exitGroup() {
    if (this.isLoggedInMemberAGroupAdmin && this.groupMemberList?.filter((member) => member.isGroupAdmin === true).length === 1) {
      this._toast.warning('You are the only admin in the group! So, You cannot leave the group', 'Warning', {
        timeOut: 6000,
        positionClass: 'toast-top-center',
      });
    } else {
      const confirmation: boolean = confirm(ConfirmationMsg.exitGroup());
      if (confirmation) {
        this.loader = true;
        this.exitGroupPayload = {
          chatGroupId: this.loadGroupChatPayload?.groupDetail.chatGroupId,
          memberId: this.user.memberId,
          updatedBy: this.user.memberId,
          isGroupAdmin: this.isLoggedInMemberAGroupAdmin,
        };
        this._internalGroupChatService.removeGroupMember(this.exitGroupPayload).subscribe(
          (response) => {
            this.loader = false;
            this.goToChatListPage();
            this._toast.success('Group exited Successfully!', 'Success', {
              timeOut: 6000,
              positionClass: 'toast-top-center',
            });
            this._internalGroupChatService.exitOrDeleteGroupSubject$.next({
              serviceRequestDetail: this.loadGroupChatPayload.serviceRequestDetail,
              groupDetail: this.loadGroupChatPayload?.groupDetail,
            });
            this._internalGroupChatService.loadGroupChatSubject$.next({
              serviceRequestDetail: null,
              groupDetail: null,
            });
          },
          (error) => {
            this.loader = false;
            this._toast.warning('Unable to exit the Group!', 'Warning', {
              timeOut: 6000,
              positionClass: 'toast-top-center',
            });
          },
        );
      }
    }
  }

  deleteGroup() {
    if (this.isDeleteGroupOptionVisible) {
      const confirmation: boolean = confirm(ConfirmationMsg.deleteGroup());
      if (confirmation) {
        this.loader = true;
        this._internalGroupChatService.deleteGroupChat(this.loadGroupChatPayload?.groupDetail.chatGroupId, this.user.memberId).subscribe(
          (response) => {
            this.loader = false;
            this.goToChatListPage();
            this._toast.success('Group deleted Successfully!', 'Success', {
              timeOut: 6000,
              positionClass: 'toast-top-center',
            });
            this._internalGroupChatService.exitOrDeleteGroupSubject$.next({
              serviceRequestDetail: this.loadGroupChatPayload.serviceRequestDetail,
              groupDetail: this.loadGroupChatPayload?.groupDetail,
            });
            this._internalGroupChatService.loadGroupChatSubject$.next({
              serviceRequestDetail: null,
              groupDetail: null,
            });
          },
          () => {
            this.loader = false;
            this._toast.warning('Unable to delete the Group!', 'Warning', {
              timeOut: 6000,
              positionClass: 'toast-top-center',
            });
          },
        );
      }
    }
  }

  updateCSSForEmojiPicker() {
    setTimeout(() => {
      $('emoji-picker').css('color', 'black');
    }, 10);
  }

  selectEmoji(event) {
    if (this.textMessageBody === null) {
      this.textMessageBody = event.char;
    } else {
      this.textMessageBody += event.char;
    }
    this.isSendButtonDisabled();
  }

  uploadMessage() {
    let sendMessageModel: ISendMessageModel;
    if (this.textMessageBody && this.textMessageBody.trim().length > 0 && this.attachment === null) {
      sendMessageModel = {
        chatGroupId: this.loadGroupChatPayload?.groupDetail.chatGroupId,
        createdBy: this.user.memberId,
        messageText: this.textMessageBody.trim(),
        messageAttachment: null,
        attachmentType: null,
      };
      this.messageList.push({
        memberId: this.user.memberId,
        messageSendTime: null,
        messageText: this.textMessageBody.trim(),
        isRead: true,
        fileName: null,
        memberName: this.user.name,
        attachmentType: null,
        messageId: null,
        memberInitials: null,
        isSending: true,
        isSentSuccessful: null,
        filePath: null,
      });
      this.textMessageBody = '';
      this.isSendButtonDisabled();
      this.scrollToElementById(null);
      this.sendMessage(sendMessageModel);
    } else if (this.attachment !== null) {
      sendMessageModel = {
        chatGroupId: this.loadGroupChatPayload?.groupDetail.chatGroupId,
        createdBy: this.user.memberId,
        messageText: this.textMessageBody && this.textMessageBody.trim().length > 0 ? this.textMessageBody.trim() : '',
        messageAttachment: this.attachmentFileName,
        attachmentType: this.attachmentType.length > 1 ? this.attachmentType[1] : this.attachmentType[0],
      };
      this.messageList.push({
        memberId: this.user.memberId,
        messageSendTime: null,
        messageText: this.textMessageBody ? this.textMessageBody.trim() : '',
        isRead: true,
        fileName: this.attachmentFileName,
        memberName: this.user.name,
        attachmentType: this.attachmentType.length > 1 ? this.attachmentType[1] : this.attachmentType[0],
        messageId: null,
        memberInitials: null,
        isSending: true,
        isSentSuccessful: null,
        filePath: this.imageURL,
      });
      this.textMessageBody = '';
      this.closeImagePreview();
      this.isSendButtonDisabled();
      this.sendMessage(sendMessageModel);
    }
  }

  sendMessage(sendMessageModel: ISendMessageModel) {
    const sentMessage = this.messageList.find((message) => message.messageId === null);
    this._internalGroupChatService.sendMessage(sendMessageModel).subscribe(
      (response) => {
        sentMessage.messageSendTime = response.data.sentMessageDateTime;
        sentMessage.messageId = response.data.messageId;
        sentMessage.isSending = false;
        sentMessage.isSentSuccessful = true;
      },
      () => {
        sentMessage.isSending = false;
        sentMessage.isSentSuccessful = false;
      },
    );
  }

  uploadImage(event: any) {
    this.attachment = event.target.files[0];
    if (
      this.attachment.type !== fileType.png &&
      this.attachment.type !== fileType.jpg &&
      this.attachment.type !== fileType.jpeg &&
      this.attachment.type !== fileType.gif
    ) {
      this._toast.warning('File type not supported. We accept JPG, JPEG, PNG and GIF only', 'Warning', {
        timeOut: 6000,
        positionClass: 'toast-top-center',
      });
    } else {
      const fileSize: number = Number((this.attachment.size / 1024 / 1024).toFixed(2));
      if (fileSize > 5) {
        this._toast.warning('File size exceeds the maximum limit (5MB)', 'Warning', {
          timeOut: 6000,
          positionClass: 'toast-top-center',
        });
      } else {
        this.imagePreview = true;
        this.isUploadFileLoading = true;
        const file = event.target['files'][0];
        const reader = new FileReader();
        reader.onload = (_) => {
          this.imageURL = reader.result as string;
        };
        reader.readAsDataURL(file);
        this.attachmentType = this.attachment.type.split('/');
        const fileInfo = new FormData();
        fileInfo.append(this.attachment.name, this.attachment);
        this.uploadFile(
          this.attachmentType.length > 1 ? this.attachmentType[1] : this.attachmentType[0],
          entityTableName.chatGroupMessage,
          DefaultGUID,
          fileInfo,
        );
      }
    }
  }

  openImagePreview(sourcePath: string, fileName?: string) {
    const previewImageConfig: PreviewFileConfig = {
      filePath: sourcePath,
      displayName: fileName,
      isDownloadButton: true,
    };
    const dialogRef = this._dialog.open(OpenImageDialogComponent, {
      height: '100%',
      width: '100%',
      panelClass: 'image-dialog',
      data: previewImageConfig,
    });
  }

  goToChatListPage() {
    if (window.innerWidth <= 991) {
      this._internalGroupChatService.toggleBetweenChatListAndMessagePanelSubject$.next(false);
    }
  }

  checkIsProfilePicAvailable(senderMemberID: string): string {
    let profilePictureUrl: string = null;
    let memberDetail: IChatGroupMembers;
    memberDetail = this.groupMemberList.find((member) => senderMemberID === member.memberId);
    if (memberDetail) {
      profilePictureUrl = memberDetail.profilePicFilePath;
    }
    return profilePictureUrl;
  }

  uploadFile(fileType: string, entityTableName: string, entityTableValue: string, fileInfo: FormData) {
    this.sendMessageBtnDisabled = true;
    this._fileUploadSubscription.add(
      this._fileService.uploadFile(fileType, entityTableName, entityTableValue, fileInfo).subscribe(
        (response) => {
          this.sendMessageBtnDisabled = false;
          this.isUploadFileLoading = false;
          this.attachmentFileName = response[0].actualName;
          this.isSendButtonDisabled();
        },
        (error) => {
          this.isUploadFileLoading = false;
          this.imagePreview = false;
          this.sendMessageBtnDisabled = false;
          this.isSendButtonDisabled();
          this._toast.error('Unable to upload the file. Please try again later!', 'Warning', {
            timeOut: 6000,
            positionClass: 'toast-top-center',
          });
        },
      ),
    );
  }

  isSendButtonDisabled() {
    if (
      (this.textMessageBody &&
        this.textMessageBody !== '' &&
        this.textMessageBody !== null &&
        this.textMessageBody.trim().length > 0 &&
        this.attachment === null) ||
      (this.attachment !== null && !this.isUploadFileLoading)
    ) {
      this.sendMessageBtnDisabled = false;
    } else {
      this.sendMessageBtnDisabled = true;
    }
  }

  closeImagePreview() {
    this.imagePreview = false;
    this.attachment = null;
    this.fileAttachment = null;
    this.attachmentFileName = null;
    this.imageURL = null;
    this._fileUploadSubscription.unsubscribe();
    this._fileUploadSubscription = new Subscription();
    this.isSendButtonDisabled();
  }
}
